import React, { useState, useCallback } from "react";
import {
  Upload as UploadIcon,
  FileText,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { uploadAPI } from "@/api";
import { useToastHelpers } from "@/hooks";
import { formatFileSize } from "@/lib/utils";

interface UploadProgress {
  file: File;
  progress: number;
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  error?: string;
  batchId?: string;
  processingMessage?: string;
  processingProgress?: number;
}

export function Upload() {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const { success, error: showError } = useToastHelpers();

  const handleFileSelect = useCallback(
    (files: FileList) => {
      const csvFiles = Array.from(files).filter(
        (file) => file.type === "text/csv" || file.name.endsWith(".csv"),
      );

      if (csvFiles.length === 0) {
        showError("Invalid file type", "Please select CSV files only");
        return;
      }

      const newUploads = csvFiles.map((file) => ({
        file,
        progress: 0,
        status: "pending" as const,
      }));

      setUploads((prev) => [...prev, ...newUploads]);

      // Start uploads
      newUploads.forEach((upload) => uploadFile(upload));
    },
    [showError],
  );

  const uploadFile = async (upload: UploadProgress) => {
    try {
      setUploads((prev) =>
        prev.map((u) =>
          u.file === upload.file ? { ...u, status: "uploading" } : u,
        ),
      );

      const response = await uploadAPI.uploadCSV(upload.file, (progress) => {
        setUploads((prev) =>
          prev.map((u) => (u.file === upload.file ? { ...u, progress } : u)),
        );
      });

      // File uploaded, now processing
      const batchId = response.data.batch_id;
      setUploads((prev) =>
        prev.map((u) =>
          u.file === upload.file
            ? {
                ...u,
                status: "processing",
                progress: 100,
                batchId,
                processingMessage: "Processing...",
                processingProgress: 0,
              }
            : u,
        ),
      );

      // Poll for processing status
      pollProcessingStatus(upload.file, batchId);
    } catch (error: any) {
      setUploads((prev) =>
        prev.map((u) =>
          u.file === upload.file
            ? { ...u, status: "error", error: error.message }
            : u,
        ),
      );
      showError("Upload failed", error.message);
    }
  };

  const pollProcessingStatus = async (file: File, batchId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await uploadAPI.getUploadStatus(batchId);
        const status = statusResponse.data;

        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? {
                  ...u,
                  processingMessage: status.progress_message,
                  processingProgress: status.progress,
                }
              : u,
          ),
        );

        // Stop polling when complete or failed
        if (status.status === "completed") {
          clearInterval(pollInterval);
          setUploads((prev) =>
            prev.map((u) =>
              u.file === file ? { ...u, status: "completed" } : u,
            ),
          );
          success("Processing completed", status.progress_message);
        } else if (
          status.status === "failed" ||
          status.status === "completed_with_errors"
        ) {
          clearInterval(pollInterval);
          setUploads((prev) =>
            prev.map((u) =>
              u.file === file
                ? {
                    ...u,
                    status: "error",
                    error: status.errors.join(", ") || "Processing failed",
                  }
                : u,
            ),
          );
          showError("Processing failed", status.errors.join(", "));
        }
      } catch (error: any) {
        clearInterval(pollInterval);
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? {
                  ...u,
                  status: "error",
                  error: "Failed to check processing status",
                }
              : u,
          ),
        );
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Upload Data</h1>
        <p className="text-muted-foreground">
          Import CSV files containing LPR session data
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload Zone */}
        <Card>
          <CardHeader>
            <CardTitle>CSV File Upload</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <UploadIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Upload CSV Files</h3>
              <p className="text-muted-foreground mb-4">
                Drag and drop files here, or click to select
              </p>
              <Button
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".csv";
                  input.multiple = true;
                  input.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (files) handleFileSelect(files);
                  };
                  input.click();
                }}
              >
                Select Files
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload Progress */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Progress</CardTitle>
          </CardHeader>
          <CardContent>
            {uploads.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No uploads in progress
              </p>
            ) : (
              <div className="space-y-4">
                {uploads.map((upload, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium truncate">
                          {upload.file.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(upload.file.size)}
                        </span>
                        {upload.status === "completed" && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                        {upload.status === "error" && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </div>

                    {/* File Upload Progress */}
                    {upload.status === "uploading" && (
                      <>
                        <Progress value={upload.progress} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          Uploading file... {upload.progress}%
                        </p>
                      </>
                    )}

                    {/* Processing Progress */}
                    {upload.status === "processing" && (
                      <>
                        <Progress
                          value={upload.processingProgress || 0}
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          {upload.processingMessage || "Processing..."} (
                          {upload.processingProgress || 0}%)
                        </p>
                      </>
                    )}

                    {/* Completed */}
                    {upload.status === "completed" && (
                      <p className="text-xs text-green-600">
                        {upload.processingMessage || "Upload complete"}
                      </p>
                    )}

                    {/* Error */}
                    {upload.error && (
                      <p className="text-xs text-red-500">{upload.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>CSV Format Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Required CSV Format</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Your CSV file must contain these exact column headers:
              </p>
              <div className="bg-muted p-3 rounded text-sm font-mono">
                License Plate, Country, State, Zone, Utc Time, Camera Id, Lane
                Type
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Example Data</h4>
              <div className="bg-muted p-3 rounded text-sm font-mono">
                ADW039, USA, NE, 503, 3/15/2025 23:58, 27, Out
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h4 className="font-medium mb-2">Column Mappings</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Zone → Parking Location</li>
                  <li>• Lane Type "In" → Vehicle Entry</li>
                  <li>• Lane Type "Out" → Vehicle Exit</li>
                  <li>• Utc Time → Read Timestamp</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Supported Formats</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Time: M/D/YYYY H:MM format</li>
                  <li>• Plates: Any format (normalized)</li>
                  <li>• Countries: 2-3 letter codes</li>
                  <li>• States: 2 letter codes</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
